'use client';

import { theme } from '@/config';
import React from 'react';
import styled from 'styled-components';

const CustomLegendContainer = styled.div<{ $gap: number; $size?: 'small' | 'medium' | 'large' }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${({ $gap }) => $gap}px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  cursor: pointer;
  /* Remove any hover effects */
  &:hover {
    border: none;
    outline: none;
  }
`;

const LegendTooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 6px 10px;
  background: #1f2937;
  color: #ffffff;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 1000;
  opacity: 0;
  transition: opacity 0.2s;
  
  ${LegendItem}:hover & {
    opacity: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #1f2937;
  }
`;

const LegendCircle = styled.div<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const LegendLabel = styled.span<{ $fontSize?: string; $fontWeight?: number }>`
  font-size: ${({ $fontSize }) => $fontSize || '0.875rem'};
  font-weight: ${({ $fontWeight }) => $fontWeight || 'normal'};
  color: ${theme.colors.gray[700]};
  white-space: nowrap;
`;

export interface LegendSeries {
  name: string;
  color?: string;
  data?: number[]; // Optional: for calculating totals in tooltip
}

export interface CustomLegendProps {
  series: LegendSeries[];
  size?: 'small' | 'medium' | 'large';
  legendTooltipLabel?: string;
  legendFontSize?: string;
  legendFontWeight?: number;
  defaultColors?: string[];
  gap?: number; // Optional: if not provided, will be calculated based on series length
  style?: React.CSSProperties;
}

export const CustomLegend: React.FC<CustomLegendProps> = ({
  series,
  size = 'medium',
  legendTooltipLabel = 'count',
  legendFontSize = '10px',
  legendFontWeight = 500,
  defaultColors = [],
  gap,
  style,
}) => {
  // Calculate gap if not provided
  const calculatedGap = gap !== undefined 
    ? gap 
    : (series?.length > 4 ? 120 - (series?.length - 4) * 70 : 48);

  return (
    <CustomLegendContainer $gap={calculatedGap} $size={size} style={style}>
      {series.map((item, index) => {
        // Calculate total count if data is provided
        const totalCount = item.data?.reduce((sum, value) => sum + (value || 0), 0) || 0;
        
        const formattedCount = totalCount >= 1000 
          ? `${(totalCount / 1000).toFixed(1)}k` 
          : totalCount.toLocaleString();
        
        const color = item.color || defaultColors[index] || theme.colors.primary[500];
        
        return (
          <LegendItem key={item.name}>
            {item.data && item.data.length > 0 && (
              <LegendTooltip>
                {legendTooltipLabel}: {formattedCount}
              </LegendTooltip>
            )}
            <LegendCircle $color={color} />
            <LegendLabel $fontSize={legendFontSize} $fontWeight={legendFontWeight}>
              {item.name}
            </LegendLabel>
          </LegendItem>
        );
      })}
    </CustomLegendContainer>
  );
};

